import React, { useState, useEffect, useMemo } from 'react';
import { Col, Text, CMSLayout, Row, Button } from 'components';
import { IScreen } from 'type';
import Store from 'store';
import { COLOR } from 'const';
import { ActivityIndicator } from 'react-native';
import { saveAs } from 'file-saver';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';


const AdminStatistics: IScreen = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [moneyData, setMoneyData] = useState(null);
  const [chartData, setChartData] = useState<any[]>([]);

  const loadAllMoney = async () => {
    setLoading(true);
    try {
      const [statsRes, chartRes] = await Promise.all([
        Store.Api.Money.statistics(),
        Store.Api.Money.chart(),
      ]);
      
      if (statsRes.data.success) {
        setMoneyData(statsRes.data.data);
        console.log('Statistics data loaded');
      } else {
        alert('Có lỗi xảy ra khi tải dữ liệu');
        console.error('Error:', statsRes.data);
      }

      if (chartRes.data && chartRes.data.success) {
        const data = chartRes.data.data?.chartData || [];
        setChartData(data);
      }
    } catch (error) {
      console.error('Error loading money:', error);
      alert('Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadAllMoney();
  }, []);

  const exportToExcel = async () => {
    setExporting(true);
    
    try {
      const token = Store.Client.getToken();
      const Request = require('store/api-client/Request.utils').default;
      const host = Request.host || 'http://localhost:3000';
      const url = `${host}/api/money/exportToExcel`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token,
        },
      });

      if (!response.ok) {
        throw new Error('Không thể tải file Excel');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `thong_ke_tien_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      saveAs(blob, fileName);
      console.log('Đã xuất file Excel thành công!');
      alert('Đã xuất file Excel thành công!');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Có lỗi xảy ra khi xuất file Excel: ' + (error.message || error));
    } finally {
      setExporting(false);
    }
  };

  const statsByValue = moneyData?.statsByValue || {};
  const statsBySaleStatus = moneyData?.statsBySaleStatus || {
    'available': 0,
    'pending-ship': 0,
    'sent-to-customer': 0,
    'not-available': 0,
  };
  const totalSold = moneyData?.totalSold || 0;
  const totalNotSold = moneyData?.totalNotSold || 0;

  const sortedStatsKeys = useMemo(() => {
    return Object.keys(statsByValue).sort((a, b) => {
      const numA = parseFloat(a.replace(/[^\d]/g, '')) || 0;
      const numB = parseFloat(b.replace(/[^\d]/g, '')) || 0;
      return numB - numA;
    });
  }, [statsByValue]);

  const chartDataWithScaled = useMemo(() => {
    return chartData.map(item => ({
      ...item,
      nhapScaled: item.nhap * 100,
      xuatScaled: item.xuat * 100,
    }));
  }, [chartData]);

  const maxValueNhap = useMemo(() => {
    if (chartDataWithScaled.length === 0) return 1000;
    const max = Math.max(...chartDataWithScaled.map(item => item.nhapScaled));
    if (max === 0) return 1000;
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    return Math.ceil(max / magnitude) * magnitude;
  }, [chartDataWithScaled]);

  const maxValueXuat = useMemo(() => {
    if (chartDataWithScaled.length === 0) return 1000;
    const max = Math.max(...chartDataWithScaled.map(item => item.xuatScaled));
    if (max === 0) return 1000;
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    return Math.ceil(max / magnitude) * magnitude;
  }, [chartDataWithScaled]);

  const xAxisTicks = useMemo(() => {
    if (chartDataWithScaled.length <= 5) {
      return chartDataWithScaled.map(item => item.dateFormatted);
    }
    const step = Math.floor(chartDataWithScaled.length / 4);
    const ticks = [];
    for (let i = 0; i < chartDataWithScaled.length; i += step) {
      ticks.push(chartDataWithScaled[i].dateFormatted);
    }
    if (ticks[ticks.length - 1] !== chartDataWithScaled[chartDataWithScaled.length - 1].dateFormatted) {
      ticks.push(chartDataWithScaled[chartDataWithScaled.length - 1].dateFormatted);
    }
    return ticks;
  }, [chartDataWithScaled]);

  return (
    <CMSLayout requireAuthen>
      <Col flex1 p2>
        <Row m1 mb2 alignItems="center">
          {moneyData && (
            <>
              <Text mr2>Tổng số tiền: {moneyData.total || 0} tờ</Text>
              <Button
                text={exporting ? 'Đang xuất...' : 'Xuất Excel'}
                onPress={exportToExcel}
                disabled={loading || exporting}
                outline={true}
                height={30}
                borderRadius={15}
                width={120}
                bgHovered={COLOR.GREY_BG}
              />
            </>
          )}
          {loading && (
            <Row ml2 alignItems="center">
              {/* @ts-ignore */}
              <ActivityIndicator size="small" color={COLOR.MAIN} />
              <Text ml1>Đang tải dữ liệu, quá trình này có thể mất ít phút...</Text>
            </Row>
          )}
          {exporting && (
            <Row ml2 alignItems="center">
              {/* @ts-ignore */}
              <ActivityIndicator size="small" color={COLOR.MAIN} />
              <Text ml1>Đang xuất...</Text>
            </Row>
          )}
        </Row>

        {/* Biểu đồ Nhập - Xuất */}
        {chartDataWithScaled.length > 0 && (
          <Col mb4>
            <Text bold mb2 fontSize={18} textAlign="center">Biểu đồ Nhập - Xuất</Text>
            <Col style={{ width: '100%', height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartDataWithScaled}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="dateFormatted" 
                    ticks={xAxisTicks}
                  />
                  <YAxis 
                    yAxisId="left"
                    domain={[0, maxValueNhap]} 
                    tickFormatter={(value) => {
                      return (value / 100).toString();
                    }}
                    tick={{ fill: '#8884d8' }}
                    label={{ value: 'Nhập', angle: 0, position: 'top', style: { textAnchor: 'middle', fill: '#8884d8' } }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    domain={[0, maxValueXuat]} 
                    tickFormatter={(value) => {
                      return (value / 100).toString();
                    }}
                    tick={{ fill: '#82ca9d' }}
                    label={{ value: 'Xuất', angle: 0, position: 'top', style: { textAnchor: 'middle', fill: '#82ca9d' } }}
                  />
                  <Tooltip 
                    formatter={(value: any, name: string) => {
                      if (name === 'Nhập (tờ)' || name === 'Xuất (tờ)') {
                        return [value / 100, name];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="nhapScaled" 
                    stroke="#8884d8" 
                    name="Nhập (tờ)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="xuatScaled" 
                    stroke="#82ca9d" 
                    name="Xuất (tờ)"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Col>
          </Col>
        )}

        {loading && !moneyData && (
          <Col mt4 alignItems="center">
            {/* @ts-ignore */}
            <ActivityIndicator size="large" color={COLOR.MAIN} />
            <Text mt2>Đang tải dữ liệu thống kê...</Text>
          </Col>
        )}

        {!loading && !moneyData && (
          <Col mt4 alignItems="center">
            <Text fontSize={16} color={COLOR.GREY}>
              Không có dữ liệu để hiển thị
            </Text>
            <Button
              text="Tải lại"
              onPress={loadAllMoney}
              outline={true}
              height={30}
              borderRadius={15}
              width={100}
              mt2
            />
          </Col>
        )}

        {moneyData && (
          <>
            {/* Thống kê theo mệnh giá */}
            <Col mt2 mb2>
              <Text fontSize={18} bold mb2>
                Thống kê số lượng tiền theo mệnh giá
              </Text>
              
              {/* Header */}
              <Row m1 p1 backgroundColor={COLOR.MAIN} round1>
                <Col flex1>
                  <Text bold colorWhite>Mệnh giá</Text>
                </Col>
                <Col flex1>
                  <Text bold colorWhite>Số lượng (tờ)</Text>
                </Col>
              </Row>

              {sortedStatsKeys.length === 0 ? (
                <Col m1 p2 alignItems="center">
                  <Text color={COLOR.GREY}>Chưa có dữ liệu</Text>
                </Col>
              ) : (
                sortedStatsKeys.map((value, i) => (
                  <Col key={value} m1 p1 backgroundColor={i % 2 === 0 ? 'rgba(255,255,255,0.8)' : '#ddd'} round1>
                    <Row mb1>
                      <Col flex1>
                        <Text>{value}</Text>
                      </Col>
                      <Col flex1>
                        <Text bold color={COLOR.MAIN}>
                          {statsByValue[value]} tờ
                        </Text>
                      </Col>
                    </Row>
                  </Col>
                ))
              )}
            </Col>

            {/* Thống kê theo trạng thái bán */}
            <Col mt2 mb2>
              <Text fontSize={18} bold mb2>
                Thống kê số lượng tiền đã bán / chưa bán
              </Text>
              
              {/* Header */}
              <Row m1 p1 backgroundColor={COLOR.MAIN} round1>
                <Col flex1>
                  <Text bold colorWhite>Trạng thái</Text>
                </Col>
                <Col flex1>
                  <Text bold colorWhite>Số lượng (tờ)</Text>
                </Col>
              </Row>

              {/* Rows */}
              <Col m1 p1 backgroundColor="rgba(255,255,255,0.8)" round1>
                <Row mb1>
                  <Col flex1>
                    <Text bold color="#28a745">Đã bán</Text>
                  </Col>
                  <Col flex1>
                    <Text bold color="#28a745">
                      {totalSold} tờ
                    </Text>
                  </Col>
                </Row>
              </Col>
              <Col m1 p1 backgroundColor="#ddd" round1>
                <Row mb1>
                  <Col flex1>
                    <Text>  - Đang chờ ship</Text>
                  </Col>
                  <Col flex1>
                    <Text>{statsBySaleStatus['pending-ship'] || 0} tờ</Text>
                  </Col>
                </Row>
              </Col>
              <Col m1 p1 backgroundColor="rgba(255,255,255,0.8)" round1>
                <Row mb1>
                  <Col flex1>
                    <Text>  - Đã bán cho khách</Text>
                  </Col>
                  <Col flex1>
                    <Text>{statsBySaleStatus['sent-to-customer'] || 0} tờ</Text>
                  </Col>
                </Row>
              </Col>

              <Col m1 p1 backgroundColor="#ddd" round1>
                <Row mb1>
                  <Col flex1>
                    <Text bold color="#17a2b8">Chưa bán</Text>
                  </Col>
                  <Col flex1>
                    <Text bold color="#17a2b8">
                      {totalNotSold} tờ
                    </Text>
                  </Col>
                </Row>
              </Col>
              <Col m1 p1 backgroundColor="rgba(255,255,255,0.8)" round1>
                <Row mb1>
                  <Col flex1>
                    <Text>  - Có sẵn</Text>
                  </Col>
                  <Col flex1>
                    <Text>{statsBySaleStatus['available'] || 0} tờ</Text>
                  </Col>
                </Row>
              </Col>
              <Col m1 p1 backgroundColor="#ddd" round1>
                <Row mb1>
                  <Col flex1>
                    <Text>  - Không có sẵn</Text>
                  </Col>
                  <Col flex1>
                    <Text>{statsBySaleStatus['not-available'] || 0} tờ</Text>
                  </Col>
                </Row>
              </Col>
            </Col>
          </>
        )}
      </Col>
    </CMSLayout>
  );
};

AdminStatistics.routeInfo = {
  title: 'Thống kê',
  path: '/admin/statistics',
};

export default AdminStatistics;